import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * cn — une clases y resuelve conflictos de Tailwind quedándose con la última.
 *
 * Sin esto, `cn('px-4', props.className)` con un `className="px-2"` deja las dos
 * en el atributo y gana la que Tailwind haya emitido antes en la hoja: el
 * resultado depende del orden del CSS generado, no del que escribe el
 * componente. Que un componente se pueda ajustar desde fuera de forma
 * predecible es lo que evita el `<div className="…">` copiado y pegado.
 *
 * ── Por qué está EXTENDIDO y no es el `twMerge` de fábrica ───────────────
 *
 * `tailwind-merge` no lee nuestro `@theme`: adivina a qué grupo pertenece cada
 * clase por su forma. Y `text-*` es ambiguo en Tailwind —vale para tamaño de
 * letra y para color de texto—, así que con nombres propios se equivoca.
 *
 * El fallo real, encontrado en pantalla: el botón primario declara
 * `text-on-brand` (color) y su variante de tamaño añade `text-3` (cuerpo).
 * `twMerge` metía los dos en «font-size», daba por hecho que se pisaban y
 * dejaba solo el último — así que el botón navy perdía su texto blanco y salía
 * con letra NEGRA sobre fondo navy. Contraste medido: 1,4. Ilegible, y sin un
 * solo error en consola que lo avisara.
 *
 * Declarar aquí las dos escalas es lo que hace que `text-on-brand` y `text-3`
 * se reconozcan como cosas distintas y convivan.
 *
 * Al añadir un color o un paso de tamaño a `tokens.css` hay que añadirlo
 * también aquí. Es el único sitio del sistema donde algo se declara dos veces,
 * y se acepta porque la alternativa —descubrirlo mirando— ya se probó.
 */

/** Los pasos de `--text-1..10` de tokens.css. */
const TAMANOS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

/** Los nombres de `--color-*` de tokens.css. */
const COLORES = [
  'brand', 'brand-strong', 'accent', 'accent-strong',
  'surface', 'sunken', 'raised', 'line', 'line-strong',
  'ink', 'ink-2', 'muted', 'on-brand',
  'ok', 'ok-ink', 'warn', 'warn-ink', 'danger', 'danger-ink', 'info', 'info-ink',
  'pink-ink', 'purple-ink', 'teal-ink',
  'field-border',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: TAMANOS }],
      'text-color': [{ text: COLORES }],
      'bg-color': [{ bg: COLORES }],
      'border-color': [{ border: COLORES }],
    },
  },
});

export function cn(...clases: ClassValue[]) {
  return twMerge(clsx(clases));
}
